import React, { useRef, useState } from "react";
import { UserFormServiceButtonCreated } from "../MyBooking/styled/DesStyled";
import DialogWrap, { useStyleFullScreen } from "../../controls/components/dialogWrap/DialogWrap";
import useEmailContent from "./hook/useGetEmailContent";
import { AdminContentBlogManagement } from "../AdminContentManage/AdminContentDialog";
import { EmailKeyword, previewContentEmail } from "../../../services/EmailSerivce";
import useUpdateContentEmail from "./hook/useUpdateContentEmail";
import {
	StyledKeywordBox,
	StyledKeywordCount,
	StyledKeywordEmpty,
	StyledKeywordGrid,
	StyledKeywordHeader,
	StyledKeywordHeaderInfo,
	StyledKeywordSearchInput,
	StyledKeywordSection,
	StyledKeywordSectionTitle,
	StyledKeywordToggleButton,
	StyledTemplateNameInput,
	StyledKeywordToolbar,
	StyledKeywordWrap,
	StyledSimpleEditorWrap,
	StyledSimpleHint,
	StyledSimpleTextarea,
	StyledTitleKeyword,
	StyledUpdateContentWrap,
	StyledInlineCode,
	StyledCompactHeaderRow,
	StyledCompactHeaderCol,
	StyledInfoIcon,
} from "./styled/StyleEmailFormat";
import { StylePersonalLabelBank } from "../AdminPaymentChannelNumber/styled/PaymentNumber";
import { filterEmailKeywords, getKeywordDescription, groupEmailKeywords } from "./keywordUtils";
import { EMAIL_KEYWORD_UI_COPY } from "./emailKeywordConfig";
import { getSimpleEmailStarter, isSimpleEmailTemplate } from "./simpleEmailTemplate";
import sanitizeHtml from "../../../utils/sanitizeHtml";
import PermissionGate from "../../controls/auth/PermissionGate";
import { push } from "@whalelo/audit-client";
import { EmailBuilder } from "./builder/EmailBuilder";
import { useBuilderBridge, EditorMode } from "./builder/useBuilderBridge";
import { groupForTemplate, getRequiredVars } from "./builder/templateGroup";
import { useEmailVersionActions } from "./hook/useEmailVersionActions";
import { EmailVersion } from "../../../services/EmailSerivce";

export default function DialogUpdateContentEmail(props: {
	openDialog: boolean;
	setOpenDialog: (value: boolean) => void;
	keywork?: EmailKeyword[];
	selectedId: number | null;
	selectedTemplate?: { id: number; name: string; subject?: string } | null;
	reloadTemplates?: () => void;
}) {
	const editorRef = useRef<any>(null);
	/** Imperative getter wired to EmailBuilder's live serializer (skips the 300 ms debounce). */
	const builderGetContentRef = useRef<(() => string) | null>(null);
	const { content, setContent } = useEmailContent({ id: props.selectedId || 0, openDialog: props.openDialog });
	const { loadUpdate } = useUpdateContentEmail({ setOpenDialog: props.setOpenDialog });
	const [templateName, setTemplateName] = useState("");
	const [templateSubject, setTemplateSubject] = useState("");
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewHtml, setPreviewHtml] = useState("");
	const [previewLoading, setPreviewLoading] = useState(false);
	const [previewError, setPreviewError] = useState("");
	const [saveAlert, setSaveAlert] = useState("");
	const [successMsg, setSuccessMsg] = useState("");

	const notifySuccess = (msg: string) => {
		setSaveAlert("");
		setSuccessMsg(msg);
		window.setTimeout(() => setSuccessMsg(""), 2600);
	};

	const bridge = useBuilderBridge(content);

	const versionActions = useEmailVersionActions({
		selectedId: props.selectedId,
		templateName,
		templateSubject,
		onPublishSuccess: () => {
			props.reloadTemplates?.();
		},
		onRestoreSuccess: (version: EmailVersion) => {
			setContent(version.content);
			bridge.resetMode(version.content);
			if (version.name) setTemplateName(version.name);
			if (version.subject) setTemplateSubject(version.subject);
		},
		setSaveAlert,
		notify: notifySuccess,
	});

	React.useEffect(() => {
		if (!props.openDialog) return;
		setTemplateName(props.selectedTemplate?.name || "");
		setTemplateSubject(props.selectedTemplate?.subject || "");
		setSaveAlert("");
	}, [props.openDialog, props.selectedTemplate?.id, props.selectedTemplate?.name, props.selectedTemplate?.subject]);

	// Reset editor mode whenever content is freshly loaded (dialog open).
	React.useEffect(() => {
		if (props.openDialog && content !== undefined) {
			bridge.resetMode(content);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.openDialog, content]);

	const getCurrentContent = () => {
		if (bridge.mode === "builder") {
			// Prefer the live imperative getter to avoid the 300 ms debounce race.
			return builderGetContentRef.current ? builderGetContentRef.current() : content || "";
		}
		if (isSimpleEmailTemplate(content)) return content || "";
		return editorRef.current?.getContent?.() || "";
	};

	const handleSave = async () => {
		// Builder publish gate: recompute live at save to avoid the debounce race.
		if (bridge.mode === "builder") {
			const liveContent = getCurrentContent();
			const group = groupForTemplate(props.selectedId);
			const liveMissing = getRequiredVars(group).filter((v) => !liveContent.includes(v));
			if (liveMissing.length > 0) {
				setSaveAlert(`Thiếu biến bắt buộc: ${liveMissing.join(", ")}`);
				return;
			}
		}
		setSaveAlert("");
		const nextContent = getCurrentContent();
		push({
			app: "admin",
			category: "ui_action",
			action: "email_template.update",
			occurred_at: new Date().toISOString(),
			severity: 2,
			summary: `Admin updating email template ${props.selectedId}`,
			meta: { template_key: String(props.selectedId ?? ""), template_name: templateName || null },
		});
		const isSuccess = await loadUpdate(props.selectedId || 0, nextContent, templateName, templateSubject.trim());
		if (isSuccess) {
			props.reloadTemplates?.();
			props.setOpenDialog(false);
		}
	};

	const handlePreview = async () => {
		const currentContent = getCurrentContent();

		try {
			setPreviewLoading(true);
			setPreviewError("");
			const result = await previewContentEmail(props.selectedId || 0, currentContent);
			setPreviewHtml(result.html || "");
			setPreviewOpen(true);
		} catch (error: any) {
			console.error(error);
			// KHÔNG dựng bản xem nhanh ở trình duyệt nữa. Bộ render riêng đó là bản
			// viết lại của engine backend nên luôn trôi lệch dần, và người duyệt mẫu
			// không có cách nào biết mình đang nhìn bản nào. Xem trước SAI nguy hiểm
			// hơn không có xem trước — thà báo lỗi để bấm lại.
			setPreviewHtml("");
			setPreviewError(
				"Không gọi được bản xem trước từ máy chủ. Bấm Xem trước lại — bản xem trước phải do máy chủ dựng thì mới đúng email thật khi gửi."
			);
			setPreviewOpen(true);
		} finally {
			setPreviewLoading(false);
		}
	};

	return (
		<>
			<DialogWrap
				login={true}
				useCustomStyles={useStyleFullScreen}
				disableEnforceFocus={true}
				disableAutoFocus={true}
				disableRestoreFocus={true}
				// Builder mode hides the whole footer to reclaim space — the header ✕ closes,
				// and "Lưu nháp"/"Xuất bản" live in the builder topbar.
				hideFooter={bridge.mode === "builder"}
				open={props.openDialog}
				onClose={() => {
					props.setOpenDialog(false);
				}}
				title={
					templateName || props.selectedTemplate?.name
						? `Update Content Email - ${templateName || props.selectedTemplate?.name}`
						: "Update Content Email"
				}
				actions={
					// Legacy HTML mode keeps only Save. Cancel removed (redundant with the
					// header ✕); Preview removed to save space.
					bridge.mode !== "builder" ? (
						<PermissionGate permission="settings.manage">
							<UserFormServiceButtonCreated onClick={handleSave}>Save</UserFormServiceButtonCreated>
						</PermissionGate>
					) : undefined
				}>
				<>
					{/* Compact two-column header: Tên template + Subject on one row */}
					<StyledCompactHeaderRow>
						<StyledCompactHeaderCol>
							<label
								htmlFor="email-template-name"
								style={{ fontSize: 12, fontWeight: 600, color: "#1f2937" }}>
								Tên template
							</label>
							<StyledTemplateNameInput
								id="email-template-name"
								style={{ marginTop: 0 }}
								value={templateName}
								onChange={(e) => setTemplateName(e.target.value)}
								placeholder="Nhập tên hiển thị của template"
							/>
						</StyledCompactHeaderCol>
						<StyledCompactHeaderCol>
							<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
								<label
									htmlFor="email-template-subject"
									style={{ fontSize: 12, fontWeight: 600, color: "#1f2937" }}>
									Subject (tiêu đề email)
								</label>
								<StyledInfoIcon
									title="Dòng tiêu đề hiển thị trong inbox khách (Gmail subject). Hỗ trợ placeholders: ${paymentDate}, ${stationName}, ${fromDate}, ${toDate}. Để trống = dùng subject mặc định của hệ thống."
									aria-label="Dòng tiêu đề hiển thị trong inbox khách (Gmail subject). Hỗ trợ placeholders: ${paymentDate}, ${stationName}, ${fromDate}, ${toDate}. Để trống = dùng subject mặc định của hệ thống.">
									ⓘ
								</StyledInfoIcon>
							</div>
							<input
								id="email-template-subject"
								type="text"
								value={templateSubject}
								onChange={(e) => setTemplateSubject(e.target.value)}
								placeholder="VD: Payment Finalized – Payout on ${paymentDate}"
								style={{
									padding: "8px 12px",
									border: "1px solid #cbd5e1",
									borderRadius: 8,
									fontSize: 14,
									outline: "none",
									fontFamily: "inherit",
									width: "100%",
									boxSizing: "border-box",
								}}
							/>
						</StyledCompactHeaderCol>
					</StyledCompactHeaderRow>
					{/* Fixed toast — must NOT sit in the form flow: an inline banner pushes
					    the whole layout down and the toolbar buttons jump under the cursor. */}
					{saveAlert || successMsg ? (
						<div
							style={{
								position: "fixed",
								top: 16,
								left: "50%",
								transform: "translateX(-50%)",
								zIndex: 1500,
								padding: "10px 18px",
								borderRadius: 8,
								boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
								background: saveAlert ? "#fef2f2" : "#ecfdf5",
								border: saveAlert ? "1px solid #fca5a5" : "1px solid #6ee7b7",
								color: saveAlert ? "#b91c1c" : "#047857",
								fontSize: 13,
								fontWeight: 600,
								maxWidth: "80vw",
							}}>
							{saveAlert || successMsg}
						</div>
					) : null}
					<InputPartnerAdmin
						content={content || ""}
						setContent={setContent}
						keywork={props.keywork}
						selectedId={props.selectedId}
						editorRef={editorRef}
						onPreview={handlePreview}
						editorMode={bridge.mode}
						setEditorMode={bridge.setMode}
						onBuilderChange={(c, missing) => {
							setContent(c);
							bridge.handleBuilderChange(c, missing);
						}}
						onPersist={handleSave}
						builderGetContentRef={builderGetContentRef}
						onSaveDraft={versionActions.handleSaveDraft}
						onPublish={versionActions.handlePublish}
						onListVersions={versionActions.handleListVersions}
						onRestore={versionActions.handleRestore}
					/>
				</>
			</DialogWrap>

			<DialogWrap
				login={true}
				disableEnforceFocus={true}
				disableAutoFocus={true}
				disableRestoreFocus={true}
				open={previewOpen}
				onClose={() => setPreviewOpen(false)}
				title="Email Preview"
				actions={
					<UserFormServiceButtonCreated onClick={() => setPreviewOpen(false)}>
						Close
					</UserFormServiceButtonCreated>
				}>
				<div style={{ padding: 16, background: "#f3f6fb" }}>
					{previewError ? (
						<div style={{ color: "#b91c1c", fontSize: 14, marginBottom: 12 }}>{previewError}</div>
					) : (
						<></>
					)}
					<div
						style={{
							display: "flex",
							gap: 18,
							alignItems: "flex-start",
							flexWrap: "wrap",
							justifyContent: "center",
							overflow: "auto",
							maxHeight: "70vh",
						}}>
						{EMAIL_PREVIEW_WIDTHS.map((w) => (
							<EmailPreviewFrame
								key={w.width}
								label={w.label}
								width={w.width}
								html={sanitizeHtml(previewHtml || "", {
									ADD_ATTR: ["target", "rel", "style"],
								})}
							/>
						))}
					</div>
				</div>
			</DialogWrap>
		</>
	);
}

/**
 * Khổ máy dùng để soi email trong khung xem.
 *
 * Trước đây khung xem chỉ có MỘT cột duy nhất, nên lỗi bố cục ở màn hình hẹp
 * không ai thấy — điển hình là hàng hai cột đè chữ ở 375px, lọt tới tận email
 * gửi khách. 375px là iPhone tiêu chuẩn, 600px là bề ngang tối đa của thân
 * email trên máy tính.
 */
const EMAIL_PREVIEW_WIDTHS: { label: string; width: number }[] = [
	{ label: "📱 Điện thoại · 375px", width: 375 },
	{ label: "💻 Máy tính · 600px", width: 600 },
];

/**
 * Một khung xem ở đúng một bề ngang.
 *
 * Dùng <iframe srcDoc> chứ không nhúng thẳng vào trang: email mang CSS riêng,
 * nhúng thẳng thì nó ăn theo bề ngang của hộp thoại (không phải bề ngang thật
 * của máy khách) và còn có thể đụng CSS của trang admin. Trong iframe thì mỗi
 * khung là một khung nhìn độc lập, đúng bằng số px ghi trên nhãn.
 */
function EmailPreviewFrame(props: { html: string; label: string; width: number }) {
	const frameRef = useRef<HTMLIFrameElement | null>(null);
	const [height, setHeight] = useState(560);

	// iframe không tự co giãn theo nội dung — phải đo sau khi nạp xong.
	const fitToContent = () => {
		const body = frameRef.current?.contentDocument?.body;
		if (body) {
			setHeight(body.scrollHeight + 24);
		}
	};

	return (
		<div
			style={{
				background: "#ffffff",
				border: "1px solid #dbe3f0",
				borderRadius: 12,
				overflow: "hidden",
				flex: "0 0 auto",
			}}>
			<div
				style={{
					font: "600 12px/1 sans-serif",
					color: "#4a5866",
					background: "#f5f7f9",
					borderBottom: "1px solid #dde3e9",
					padding: "8px 10px",
				}}>
				{props.label}
			</div>
			<iframe
				ref={frameRef}
				title={props.label}
				srcDoc={props.html}
				onLoad={fitToContent}
				sandbox=""
				style={{ display: "block", border: 0, width: props.width, height, background: "#ffffff" }}
			/>
		</div>
	);
}

export function InputPartnerAdmin(props: {
	content: string;
	setContent: (val: string) => void;
	keywork?: EmailKeyword[];
	selectedId: number | null;
	editorRef: React.MutableRefObject<any>;
	onPreview: () => void;
	editorMode: EditorMode;
	setEditorMode: (m: EditorMode) => void;
	onBuilderChange: (content: string, missing: string[]) => void;
	/** Persists the template (same as the dialog's Save) — wired to the builder's "Lưu". */
	onPersist?: () => void;
	/** Ref wired to EmailBuilder's live content getter (skips the 300 ms debounce). */
	builderGetContentRef?: React.MutableRefObject<(() => string) | null>;
	/** Version-history handlers — forwarded straight to EmailBuilder. */
	onSaveDraft?: (content: string) => void;
	onPublish?: (content: string) => void;
	onListVersions?: () => Promise<EmailVersion[]>;
	onRestore?: (versionId: number) => void;
}) {
	const [copiedKeyword, setCopiedKeyword] = useState<string | null>(null);
	const [keywordSearch, setKeywordSearch] = useState("");
	const [showKeywords, setShowKeywords] = useState(true);
	const allKeywords = props.keywork || [];
	const filteredKeywords = filterEmailKeywords(allKeywords, keywordSearch);
	const groupedKeywords = groupEmailKeywords(filteredKeywords);
	const handleCopy = (text: string) => {
		navigator.clipboard.writeText(text).then(() => {
			setCopiedKeyword(text);
			setTimeout(() => setCopiedKeyword(null), 1500);
		});
	};

	const handleRegenerateBuilder = () => {
		// Destructive: replaces the legacy raw-HTML content with a builder-editable
		// starter. The original is still recoverable until Save (Cancel discards).
		if (
			!window.confirm(
				"Tạo lại sẽ thay nội dung HTML hiện tại bằng mẫu Builder mới. Nội dung cũ sẽ mất nếu bạn lưu. Tiếp tục?"
			)
		) {
			return;
		}
		const starter = getSimpleEmailStarter(props.selectedId);
		props.setContent(starter);
		props.setEditorMode("builder");
	};

	return (
		<StyledUpdateContentWrap>
			{/* Builder là editor duy nhất — Easy/HTML toggle đã bỏ (gây loạn nút lưu +
			    TinyMCE phá markup). Template legacy HTML thô vẫn rơi vào nhánh html
			    bên dưới (TinyMCE) kèm nút "Tạo lại bằng Builder". */}

			{/* Builder mode */}
			{props.editorMode === "builder" && (
				<EmailBuilder
					content={props.content}
					group={groupForTemplate(props.selectedId)}
					keywords={props.keywork}
					onSave={() => (props.onPersist ? props.onPersist() : undefined)}
					onChange={props.onBuilderChange}
					getContentRef={props.builderGetContentRef}
					onSaveDraft={props.onSaveDraft}
					onPublish={props.onPublish}
					onListVersions={props.onListVersions}
					onRestore={props.onRestore}
					onRegenerate={handleRegenerateBuilder}
					templateId={props.selectedId || undefined}
				/>
			)}

			{/* HTML (TinyMCE) mode */}
			{props.editorMode === "html" &&
				(isSimpleEmailTemplate(props.content) ? (
					// Builder/Easy markup ([[WL-EMAIL]]) must NEVER pass through TinyMCE:
					// it collapses newlines and wraps the markup in HTML, silently breaking
					// the template so the Builder tab can no longer open it. Show the raw
					// markup in a plain textarea instead — round-trip safe.
					<StyledSimpleEditorWrap>
						<StyledSimpleHint>
							Template này đang ở định dạng Builder (<StyledInlineCode>[[WL-EMAIL]]</StyledInlineCode>
							). Bên dưới là markup thô.
						</StyledSimpleHint>
						<StyledSimpleTextarea
							value={props.content || ""}
							onChange={(event) => props.setContent(event.target.value)}
							spellCheck={false}
						/>
					</StyledSimpleEditorWrap>
				) : (
					<>
						<StyledSimpleHint>
							Template này còn ở dạng HTML cũ — sửa trực tiếp bên dưới, hoặc{" "}
							<StyledKeywordToggleButton
								type="button"
								onClick={handleRegenerateBuilder}>
								Tạo lại bằng Builder
							</StyledKeywordToggleButton>
						</StyledSimpleHint>
						<AdminContentBlogManagement
							dialogType="email"
							content={props.content}
							setContent={props.setContent}
							editorRef={props.editorRef}
						/>
					</>
				))}

			{/* Keyword panel — shown for easy and html modes */}
			{props.editorMode !== "builder" && allKeywords.length > 0 && (
				<div>
					<StyledKeywordHeader>
						<StyledKeywordHeaderInfo>
							<StylePersonalLabelBank>{EMAIL_KEYWORD_UI_COPY.sectionTitle}</StylePersonalLabelBank>
							<StyledKeywordCount>
								{EMAIL_KEYWORD_UI_COPY.countText(filteredKeywords.length, allKeywords.length)}
							</StyledKeywordCount>
						</StyledKeywordHeaderInfo>
						<StyledKeywordToolbar>
							<StyledKeywordSearchInput
								value={keywordSearch}
								onChange={(event) => setKeywordSearch(event.target.value)}
								placeholder={EMAIL_KEYWORD_UI_COPY.searchPlaceholder}
							/>
							<StyledKeywordToggleButton onClick={() => setShowKeywords((prev) => !prev)}>
								{showKeywords ? EMAIL_KEYWORD_UI_COPY.collapseText : EMAIL_KEYWORD_UI_COPY.expandText}
							</StyledKeywordToggleButton>
						</StyledKeywordToolbar>
					</StyledKeywordHeader>
					{showKeywords && (
						<StyledKeywordWrap>
							{groupedKeywords.length > 0 ? (
								groupedKeywords.map((group) => (
									<StyledKeywordSection key={group.label}>
										<StyledKeywordSectionTitle>
											{group.label}
											<span>{group.items.length}</span>
										</StyledKeywordSectionTitle>
										<StyledKeywordGrid>
											{group.items.map((item) => (
												<StyledKeywordBox
													key={item.name}
													type="button"
													isChecked={copiedKeyword === item.name}
													onClick={() => handleCopy(item.name)}
													title={`Copy ${item.name}`}>
													<div className="top">
														<StyledTitleKeyword>{item.name}</StyledTitleKeyword>
														<i
															className="fa fa-copy"
															title="Copy"
														/>
													</div>
													<span className="desc">
														{getKeywordDescription(item) ||
															EMAIL_KEYWORD_UI_COPY.noDescription}
													</span>
												</StyledKeywordBox>
											))}
										</StyledKeywordGrid>
									</StyledKeywordSection>
								))
							) : (
								<StyledKeywordEmpty>{EMAIL_KEYWORD_UI_COPY.emptyState}</StyledKeywordEmpty>
							)}
						</StyledKeywordWrap>
					)}
				</div>
			)}
		</StyledUpdateContentWrap>
	);
}
